/**
 * ScoreExplanation — small expandable "Why this score?" panel reusable
 * across every DHI score, so users always understand:
 *   (a) what data is used,
 *   (b) what formula was applied,
 *   (c) which factors moved the score for *them* specifically.
 *
 * Educational by design — appears as a small "?" trigger that expands an
 * inline explanation. Never blocks the actual score from being read.
 */
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HelpCircle } from "lucide-react";

export default function ScoreExplanation({
  isDark = false,
  lang = "bg",
  inputs,         // string — what inputs are used
  formula,        // string — how it's calculated (plain language)
  factors,        // optional string[] — why THIS user's score (top contributors)
  testid,
}) {
  const [open, setOpen] = useState(false);
  const bg = lang === "bg";
  const t = bg
    ? {
        why: "Защо тази оценка?",
        inputs: "Какво се ползва",
        formula: "Как се изчислява",
        factors: "Защо точно тази стойност за вас",
        disclaimer: "Образователна оценка — не медицински показател.",
      }
    : {
        why: "Why this score?",
        inputs: "What's used",
        formula: "How it's calculated",
        factors: "Why this value for you",
        disclaimer: "Educational score — not a medical metric.",
      };

  return (
    <div className="mt-2" data-testid={testid || "score-explanation"}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1 text-[10px] font-semibold opacity-70 hover:opacity-100 transition-opacity ${
          isDark ? "text-cyan-200" : "text-cyan-700"
        }`}
      >
        <HelpCircle className="w-3 h-3" />
        {t.why}
        <span className={`text-[9px] transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div
              className={`mt-2 p-3 rounded-lg text-[11px] leading-relaxed space-y-2 ${
                isDark ? "bg-white/[0.03] border border-white/10" : "bg-gray-50 border border-gray-200"
              }`}
            >
              {inputs && (
                <div>
                  <strong className={isDark ? "text-cyan-200" : "text-cyan-700"}>{t.inputs}:</strong>{" "}
                  <span className="opacity-80">{inputs}</span>
                </div>
              )}
              {formula && (
                <div>
                  <strong className={isDark ? "text-amber-200" : "text-amber-700"}>{t.formula}:</strong>{" "}
                  <span className="opacity-80">{formula}</span>
                </div>
              )}
              {factors && factors.length > 0 && (
                <div>
                  <strong className={isDark ? "text-emerald-200" : "text-emerald-700"}>{t.factors}:</strong>
                  <ul className="mt-1 space-y-0.5">
                    {factors.map((f, i) => (
                      <li key={i} className="flex items-start gap-1.5 opacity-80">
                        <span className="mt-0.5">·</span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="text-[9px] opacity-50 italic">{t.disclaimer}</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
